// frontend/src/components/projects/ProjectModal.tsx - COMPLETE FIXED VERSION
// CRITICAL FIXES: Field name mapping, priority validation, and form data structure
// PRESERVED: All advanced features, tabs, conditions, exclusions, keywords management

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { cn } from '@/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import type { 
  Project, 
  Group, 
  Organization, 
  NormalizedUser,
  Priority
} from '@/api/types';

// Define project-specific types since they don't exist in API types
interface ProjectConditions {
  sender: string[];
  subject: string[];
  body: string[];
  attachmentName: string[];
  attachmentContent: string[];
}

interface ProjectExclusionConditions {
  sender: string[];
  senderDomain: string[];
  subject: string[];
  body: string[];
  attachmentName: string[];
  attachmentContent: string[];
}

// FIXED: Updated form data type to match backend expectations
interface FormDataType {
  name: string; // Frontend display name (maps to projectName in backend)
  description: string;
  department: string; // Extended property not in base Project
  organizationId: string;
  visibilityGroups: string[];
  conditions: ProjectConditions; // Extended property
  exclusionConditions: ProjectExclusionConditions; // Extended property
  keywords: string[]; // Extended property
  priority: ProjectPriority; // ✅ FIXED: Use specific project priorities
  conditionType: 'union' | 'intersection'; // Extended property
  scope: 'organization' | 'user'; // Extended property
  isActive: boolean;
  isPublic: boolean; // Use actual Project property
}

// ✅ FIXED: Define project-specific priority type to match backend validation
type ProjectPriority = 'low' | 'normal' | 'high' | 'critical';

interface ProjectModalProps {
  project?: Project;
  groups: Group[];
  organizations: Organization[];
  groupsApiAvailable: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSave: (projectData: Partial<Project> & { organizationId?: string; projectName?: string }) => Promise<void>;
  loading: boolean;
  currentUser: NormalizedUser | null;
  selectedOrganization?: string;
}

// Array input component for conditions and exclusions - MOVED OUTSIDE to prevent recreation on every render
const ArrayInput: React.FC<{
  label: string;
  field: string;
  items: string[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  placeholder: string;
  colorClass?: string;
  isFormDisabled?: boolean;
}> = ({ label, field, items, inputValue, onInputChange, onAdd, onRemove, placeholder, colorClass = "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400", isFormDisabled = false }) => (
  <div className="space-y-3">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <div className="flex space-x-2">
      <Input
        type="text"
        value={inputValue}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd())}
        placeholder={placeholder}
        className="flex-1"
        disabled={isFormDisabled}
      />
      <Button
        type="button"
        onClick={onAdd}
        size="sm"
        disabled={!inputValue.trim() || isFormDisabled}
      >
        Add
      </Button>
    </div>
    {items.length > 0 && (
      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
        {items.map((item, index) => (
          <span
            key={`${field}-${index}-${item}`}
            className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${colorClass}`}
          >
            <span className="max-w-40 truncate" title={item}>{item}</span>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="ml-1 hover:opacity-70 focus:outline-none"
              title="Remove item"
              disabled={isFormDisabled}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    )}
  </div>
);

  // ✅ FIXED: Default form data with valid priority value
  const defaultFormData: FormDataType = {
    name: '',
    description: '',
    department: '',
    organizationId: '',
    visibilityGroups: [],
    conditions: {
      sender: [],
      subject: [],
      body: [],
      attachmentName: [],
      attachmentContent: []
    },
    exclusionConditions: {
      sender: [],
      senderDomain: [],
      subject: [],
      body: [],
      attachmentName: [],
      attachmentContent: []
    },
    keywords: [],
    priority: 'normal' as ProjectPriority, // ✅ CHANGED FROM 'medium' TO 'normal' with proper typing
    conditionType: 'union',
    scope: 'organization',
    isActive: true,
    isPublic: false
  };

export const ProjectModal: React.FC<ProjectModalProps> = ({
  project,
  groups,
  organizations,
  groupsApiAvailable,
  isOpen,
  onClose,
  onSave,
  loading,
  currentUser,
  selectedOrganization
}) => {
  const [formData, setFormData] = useState<FormDataType>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'basic' | 'conditions' | 'exclusions' | 'keywords' | 'visibility'>('basic');
  const [conditionInputs, setConditionInputs] = useState<Record<string, string>>({});
  const [exclusionInputs, setExclusionInputs] = useState<Record<string, string>>({});
  const [keywordInput, setKeywordInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to safely extract organization ID
  const extractOrganizationId = useCallback((org: string | { _id?: string; id?: string } | null | undefined): string => {
    if (!org) return '';
    if (typeof org === 'string') return org;
    return (org as any)._id || (org as any).id || '';
  }, []);

  // Filter organizations based on user role
  const availableOrganizations = useMemo(() => {
    if (currentUser?.role === 'implementor') {
      return organizations;
    }
    // Non-implementors can only see their own organization
    if (currentUser?.organization) {
      const userOrgId = extractOrganizationId(currentUser.organization);
      return organizations.filter(org => ((org as any)._id || (org as any).id) === userOrgId);
    }
    return [];
  }, [organizations, currentUser, extractOrganizationId]);

  // Form validation with correct field names and priority values
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Project name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.length > 200) { // ✅ Updated to match backend limit
      newErrors.name = 'Project name cannot exceed 200 characters';
    }

    // Description validation
    if (formData.description && formData.description.length > 2000) {
      newErrors.description = 'Description cannot exceed 2000 characters';
    }

    // Department validation
    if (formData.department && formData.department.length > 100) {
      newErrors.department = 'Department name cannot exceed 100 characters';
    }

    // Organization validation for implementors creating new projects
    if (currentUser?.role === 'implementor' && !project && !formData.organizationId) {
      newErrors.organizationId = 'Organization selection is required';
    }

    // Keywords validation
    if (formData.keywords && formData.keywords.length > 100) {
      newErrors.keywords = 'Cannot have more than 100 keywords';
    }

    // ✅ FIXED: Priority validation with correct values
    const validPriorities: ProjectPriority[] = ['low', 'normal', 'high', 'critical'];
    if (!validPriorities.includes(formData.priority)) {
      newErrors.priority = 'Priority must be low, normal, high, or critical';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, currentUser, project]);

  // Reset form data when modal opens/closes
  const resetForm = useCallback(() => {
    let initialData = { ...defaultFormData };

    if (project) {
      const projectAny = project as any; // Type assertion for extended properties
      
      initialData = {
        // The list endpoint returns `projectName`; only some callers map it
        // to `name`. Without this the edit form opened with an empty name and
        // every save failed validation (customer finding #11).
        name: project.name || projectAny.projectName || '',
        description: project.description || '',
        department: projectAny.department || '', // Extended property
        organizationId: extractOrganizationId(project.organization),
        visibilityGroups: Array.isArray(project.visibilityGroups) 
          ? project.visibilityGroups.map(g => typeof g === 'string' ? g : g._id || g.id || '').filter(Boolean)
          : [],
        conditions: projectAny.conditions || {
          sender: [], subject: [], body: [], attachmentName: [], attachmentContent: []
        },
        exclusionConditions: projectAny.exclusionConditions || {
          sender: [], senderDomain: [], subject: [], body: [], attachmentName: [], attachmentContent: []
        },
        keywords: projectAny.keywords || [],
        // ✅ FIXED: Ensure priority is valid, default to 'normal'
        priority: (project.priority && ['low', 'normal', 'high', 'critical'].includes(project.priority))
          ? project.priority as ProjectPriority
          : 'normal' as ProjectPriority,
        conditionType: projectAny.conditionType || 'union',
        scope: projectAny.scope || 'organization',
        isActive: project.isActive ?? true,
        isPublic: project.isPublic ?? false
      };
    } else {
      // For new projects, determine default organization
      // Use selectedOrganization prop first, then fallback to user's org or first available
      const defaultOrgId = selectedOrganization ||
        (currentUser?.role === 'implementor' && organizations.length > 0
          ? organizations[0]._id || organizations[0].id || ''
          : extractOrganizationId(currentUser?.organization) || '');

      initialData.organizationId = defaultOrgId;
    }

    setFormData(initialData);
    setErrors({});
    setConditionInputs({});
    setExclusionInputs({});
    setKeywordInput('');
  }, [project, organizations, currentUser, extractOrganizationId, selectedOrganization]);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // ✅ CRITICAL FIX: Enhanced handleSave function with correct field mapping
  const handleSave = useCallback(async (): Promise<void> => {
    if (isSubmitting) return;

    if (!validateForm()) {
      // The failing field may be on another tab (a user adding a visibility
      // group sees the Save button do nothing). Go to it and say why.
      setActiveTab('basic');
      setErrors(prev => ({
        ...prev,
        general: prev.general || 'Please correct the highlighted field(s) before saving.'
      }));
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Determine the correct organization ID
      let organizationId = formData.organizationId;
      
      // For implementors creating new projects
      if (currentUser?.role === 'implementor' && !project) {
        if (!organizationId && selectedOrganization && selectedOrganization !== 'all') {
          organizationId = selectedOrganization;
        }
        
        // Validation: Implementors MUST have organization ID for new projects
        if (!organizationId) {
          setErrors({ organizationId: 'Organization selection is required for implementors' });
          return;
        }
      }
      
      // For editing existing projects, use the project's organization
      if (project && !organizationId) {
        organizationId = extractOrganizationId(project.organization);
      }
      
      // For non-implementors, use their organization
      if (!organizationId && currentUser?.organization) {
        organizationId = extractOrganizationId(currentUser.organization);
      }

      // ✅ CRITICAL FIX: Prepare project data with correct field mapping for backend
      const projectData: Partial<Project> & { 
        organizationId?: string; 
        projectName?: string; // Backend expects 'projectName'
      } = {
        // ✅ Backend API expects 'projectName' field
        projectName: formData.name.trim(),
        
        // ✅ Also include 'name' for frontend compatibility
        name: formData.name.trim(),
        
        description: formData.description.trim() || undefined,
        priority: formData.priority as Priority, // ✅ Cast to API Priority type for compatibility
        isActive: formData.isActive,
        isPublic: formData.isPublic,
        visibilityGroups: formData.visibilityGroups.filter(Boolean),
        
        // Extended properties (will be handled by backend if supported)
        ...(formData.department && { department: formData.department.trim() }),
        ...(formData.conditions && { conditions: formData.conditions }),
        ...(formData.exclusionConditions && { exclusionConditions: formData.exclusionConditions }),
        ...(formData.keywords.length > 0 && { keywords: formData.keywords }),
        ...(formData.conditionType && { conditionType: formData.conditionType }),
        ...(formData.scope && { scope: formData.scope })
      };

      // Include organization ID for API
      if (organizationId) {
        projectData.organizationId = organizationId;
      }


      await onSave(projectData);
      onClose();
    } catch (error: any) {
      console.error('ProjectModal: Save failed:', error);
      const msg: string = error?.message || '';
      const timedOut = /timeout|network error|ECONNABORTED/i.test(msg) || error?.code === 'ECONNABORTED';
      setErrors({
        general: timedOut
          ? 'The server did not answer in time. Your changes were not saved — please try again in a moment.'
          : (msg || 'Failed to save project. Please try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, isSubmitting, currentUser, project, selectedOrganization, onSave, onClose, extractOrganizationId]);

  // Generic input change handler with error clearing
  const handleInputChange = useCallback((field: keyof FormDataType, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  // Condition management functions
  const addCondition = useCallback((field: keyof ProjectConditions, value: string) => {
    if (!value.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: [...(prev.conditions[field] || []), value.trim()]
      }
    }));
    setConditionInputs(prev => ({ ...prev, [field]: '' }));
  }, []);

  const removeCondition = useCallback((field: keyof ProjectConditions, index: number) => {
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: prev.conditions[field]?.filter((_, i) => i !== index) || []
      }
    }));
  }, []);

  // Exclusion management functions  
  const addExclusion = useCallback((field: keyof ProjectExclusionConditions, value: string) => {
    if (!value.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      exclusionConditions: {
        ...prev.exclusionConditions,
        [field]: [...(prev.exclusionConditions[field] || []), value.trim()]
      }
    }));
    setExclusionInputs(prev => ({ ...prev, [field]: '' }));
  }, []);

  const removeExclusion = useCallback((field: keyof ProjectExclusionConditions, index: number) => {
    setFormData(prev => ({
      ...prev,
      exclusionConditions: {
        ...prev.exclusionConditions,
        [field]: prev.exclusionConditions[field]?.filter((_, i) => i !== index) || []
      }
    }));
  }, []);

  // Keyword management
  const addKeyword = useCallback(() => {
    const keyword = keywordInput.trim();
    if (!keyword || formData.keywords.includes(keyword)) return;
    
    setFormData(prev => ({
      ...prev,
      keywords: [...prev.keywords, keyword]
    }));
    setKeywordInput('');
  }, [keywordInput, formData.keywords]);

  const removeKeyword = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index)
    }));
  }, []);

  // Visibility group toggle
  const handleGroupToggle = useCallback((groupId: string) => {
    setFormData(prev => ({
      ...prev,
      visibilityGroups: prev.visibilityGroups.includes(groupId)
        ? prev.visibilityGroups.filter(id => id !== groupId)
        : [...prev.visibilityGroups, groupId]
    }));
  }, []);

  const isFormDisabled = loading || isSubmitting;

  // ✅ FIXED: Priority options with correct values (removed 'medium', added 'critical')
  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'normal', label: 'Normal', color: 'text-blue-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'critical', label: 'Critical', color: 'text-red-600' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4">
      <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

      <div className="relative w-full max-w-4xl max-h-[90vh] text-left transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg flex flex-col z-10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {project ? 'Edit Project' : 'Create New Project'}
            </h2>
            {currentUser?.role === 'implementor' && (
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                System Administrator - Can create projects for any organization
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isFormDisabled}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Show general errors at the top */}
        {errors.general && (
          <div className="px-6 py-2">
            <ErrorAlert
              message={errors.general}
              onClose={() => setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.general;
                return newErrors;
              })}
            />
          </div>
        )}

        {/* Tab Navigation */}
        <div className="px-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8" role="tablist" aria-label="Project details">
            {[
              { id: 'basic', label: 'Basic Info' },
              { id: 'conditions', label: 'Match Conditions' },
              { id: 'exclusions', label: 'Exclusions' },
              { id: 'keywords', label: 'Keywords' },
              { id: 'visibility', label: 'Visibility (Optional)' }
            ].map((tab) => (
              <button
                key={tab.id}
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`tabpanel-${tab.id}`}
                onClick={() => setActiveTab(tab.id as any)}
                disabled={isFormDisabled}
                className={cn(
                  "py-2 px-1 border-b-2 font-medium text-sm transition-colors",
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300",
                  isFormDisabled && "cursor-not-allowed opacity-50"
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="p-6 space-y-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
                <div role="tabpanel" id="tabpanel-basic" aria-labelledby="tab-basic" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Project Name */}
                  <div className="lg:col-span-2">
                    <Input
                      label="Project Name *"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter project name"
                      maxLength={200} // ✅ Updated to match backend limit
                      disabled={isFormDisabled}
                      error={errors.name}
                      required
                    />
                    {formData.name.length > 180 && (
                      <p className="text-xs text-orange-600 mt-1">
                        {200 - formData.name.length} characters remaining
                      </p>
                    )}
                  </div>

                  {/* Priority - FIXED with correct options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={isFormDisabled}
                    >
                      {priorityOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.priority && (
                      <p className="text-sm text-red-600 mt-1">{errors.priority}</p>
                    )}
                  </div>

                  {/* Department */}
                  <div>
                    <Input
                      label="Department (Optional)"
                      type="text"
                      value={formData.department}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      placeholder="Enter department"
                      maxLength={100}
                      disabled={isFormDisabled}
                      error={errors.department}
                    />
                  </div>

                  {/* Description */}
                  <div className="lg:col-span-2">
                    <Textarea
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Enter project description (optional)"
                      maxLength={2000}
                      disabled={isFormDisabled}
                      error={errors.description}
                      rows={4}
                    />
                    {formData.description.length > 1800 && (
                      <p className="text-xs text-orange-600 mt-1">
                        {2000 - formData.description.length} characters remaining
                      </p>
                    )}
                  </div>

                  {/* Organization Selection (for implementors) */}
                  {currentUser?.role === 'implementor' && !project && (
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Organization *
                      </label>
                      <select
                        value={formData.organizationId}
                        onChange={(e) => handleInputChange('organizationId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        disabled={isFormDisabled}
                      >
                        <option value="">Select Organization</option>
                        {availableOrganizations.map(org => (
                          <option key={org.id || org._id} value={org.id || org._id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                      {errors.organizationId && (
                        <p className="text-sm text-red-600 mt-1">{errors.organizationId}</p>
                      )}
                    </div>
                  )}

                  {/* Active Status */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          className="mr-2"
                          disabled={isFormDisabled}
                        />
                        <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
                          Project is active
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isPublic"
                          checked={formData.isPublic}
                          onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                          className="mr-2"
                          disabled={isFormDisabled}
                        />
                        <label htmlFor="isPublic" className="text-sm text-gray-700 dark:text-gray-300">
                          Public project
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Condition Type and Scope */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Condition Type
                    </label>
                    <select
                      value={formData.conditionType}
                      onChange={(e) => handleInputChange('conditionType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={isFormDisabled}
                    >
                      <option value="union">Union (Any condition matches)</option>
                      <option value="intersection">Intersection (All conditions must match)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Scope
                    </label>
                    <select
                      value={formData.scope}
                      onChange={(e) => handleInputChange('scope', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={isFormDisabled}
                    >
                      <option value="organization">Organization-wide</option>
                      <option value="user">User-specific</option>
                    </select>
                  </div>
              </div>
            )}

            {/* Conditions Tab */}
            {activeTab === 'conditions' && (
              <div role="tabpanel" id="tabpanel-conditions" aria-labelledby="tab-conditions" className="space-y-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Define conditions that emails must match to be automatically assigned to this project.
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ArrayInput
                    label="Sender Conditions"
                    field="sender"
                    items={formData.conditions.sender}
                    inputValue={conditionInputs.sender || ''}
                    onInputChange={(value) => setConditionInputs(prev => ({ ...prev, sender: value }))}
                    onAdd={() => addCondition('sender', conditionInputs.sender || '')}
                    onRemove={(index) => removeCondition('sender', index)}
                    placeholder="e.g., @company.com or john@example.com"
                    isFormDisabled={isFormDisabled}
                  />

                  <ArrayInput
                    label="Subject Conditions"
                    field="subject"
                    items={formData.conditions.subject}
                    inputValue={conditionInputs.subject || ''}
                    onInputChange={(value) => setConditionInputs(prev => ({ ...prev, subject: value }))}
                    onAdd={() => addCondition('subject', conditionInputs.subject || '')}
                    onRemove={(index) => removeCondition('subject', index)}
                    placeholder="e.g., Invoice, Support Request"
                    isFormDisabled={isFormDisabled}
                  />

                  <ArrayInput
                    label="Body Content Conditions"
                    field="body"
                    items={formData.conditions.body}
                    inputValue={conditionInputs.body || ''}
                    onInputChange={(value) => setConditionInputs(prev => ({ ...prev, body: value }))}
                    onAdd={() => addCondition('body', conditionInputs.body || '')}
                    onRemove={(index) => removeCondition('body', index)}
                    placeholder="e.g., urgent, payment due"
                    isFormDisabled={isFormDisabled}
                  />

                  <ArrayInput
                    label="Attachment Name Conditions"
                    field="attachmentName"
                    items={formData.conditions.attachmentName}
                    inputValue={conditionInputs.attachmentName || ''}
                    onInputChange={(value) => setConditionInputs(prev => ({ ...prev, attachmentName: value }))}
                    onAdd={() => addCondition('attachmentName', conditionInputs.attachmentName || '')}
                    onRemove={(index) => removeCondition('attachmentName', index)}
                    placeholder="e.g., invoice.pdf, contract"
                    isFormDisabled={isFormDisabled}
                  />

                  <div className="lg:col-span-2">
                    <ArrayInput
                      label="Attachment Content Conditions"
                      field="attachmentContent"
                      items={formData.conditions.attachmentContent}
                      inputValue={conditionInputs.attachmentContent || ''}
                      onInputChange={(value) => setConditionInputs(prev => ({ ...prev, attachmentContent: value }))}
                      onAdd={() => addCondition('attachmentContent', conditionInputs.attachmentContent || '')}
                      onRemove={(index) => removeCondition('attachmentContent', index)}
                      placeholder="e.g., total amount, project name"
                      isFormDisabled={isFormDisabled}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Exclusions Tab */}
            {activeTab === 'exclusions' && (
              <div role="tabpanel" id="tabpanel-exclusions" aria-labelledby="tab-exclusions" className="space-y-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Define conditions that will prevent emails from being assigned to this project, even if they match other conditions.
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ArrayInput
                    label="Exclude Senders"
                    field="sender"
                    items={formData.exclusionConditions.sender}
                    inputValue={exclusionInputs.sender || ''}
                    onInputChange={(value) => setExclusionInputs(prev => ({ ...prev, sender: value }))}
                    onAdd={() => addExclusion('sender', exclusionInputs.sender || '')}
                    onRemove={(index) => removeExclusion('sender', index)}
                    placeholder="e.g., noreply@spam.com"
                    colorClass="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    isFormDisabled={isFormDisabled}
                  />

                  <ArrayInput
                    label="Exclude Sender Domains"
                    field="senderDomain"
                    items={formData.exclusionConditions.senderDomain}
                    inputValue={exclusionInputs.senderDomain || ''}
                    onInputChange={(value) => setExclusionInputs(prev => ({ ...prev, senderDomain: value }))}
                    onAdd={() => addExclusion('senderDomain', exclusionInputs.senderDomain || '')}
                    onRemove={(index) => removeExclusion('senderDomain', index)}
                    placeholder="e.g., spam.com"
                    colorClass="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    isFormDisabled={isFormDisabled}
                  />

                  <ArrayInput
                    label="Exclude Subject Terms"
                    field="subject"
                    items={formData.exclusionConditions.subject}
                    inputValue={exclusionInputs.subject || ''}
                    onInputChange={(value) => setExclusionInputs(prev => ({ ...prev, subject: value }))}
                    onAdd={() => addExclusion('subject', exclusionInputs.subject || '')}
                    onRemove={(index) => removeExclusion('subject', index)}
                    placeholder="e.g., unsubscribe, spam"
                    colorClass="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    isFormDisabled={isFormDisabled}
                  />

                  <ArrayInput
                    label="Exclude Body Content"
                    field="body"
                    items={formData.exclusionConditions.body}
                    inputValue={exclusionInputs.body || ''}
                    onInputChange={(value) => setExclusionInputs(prev => ({ ...prev, body: value }))}
                    onAdd={() => addExclusion('body', exclusionInputs.body || '')}
                    onRemove={(index) => removeExclusion('body', index)}
                    placeholder="e.g., advertisement, promotion"
                    colorClass="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    isFormDisabled={isFormDisabled}
                  />

                  <ArrayInput
                    label="Exclude Attachment Names"
                    field="attachmentName"
                    items={formData.exclusionConditions.attachmentName}
                    inputValue={exclusionInputs.attachmentName || ''}
                    onInputChange={(value) => setExclusionInputs(prev => ({ ...prev, attachmentName: value }))}
                    onAdd={() => addExclusion('attachmentName', exclusionInputs.attachmentName || '')}
                    onRemove={(index) => removeExclusion('attachmentName', index)}
                    placeholder="e.g., virus.exe"
                    colorClass="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    isFormDisabled={isFormDisabled}
                  />

                  <div className="lg:col-span-2">
                    <ArrayInput
                      label="Exclude Attachment Content"
                      field="attachmentContent"
                      items={formData.exclusionConditions.attachmentContent}
                      inputValue={exclusionInputs.attachmentContent || ''}
                      onInputChange={(value) => setExclusionInputs(prev => ({ ...prev, attachmentContent: value }))}
                      onAdd={() => addExclusion('attachmentContent', exclusionInputs.attachmentContent || '')}
                      onRemove={(index) => removeExclusion('attachmentContent', index)}
                      placeholder="e.g., confidential"
                      colorClass="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                      isFormDisabled={isFormDisabled}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Keywords Tab */}
            {activeTab === 'keywords' && (
              <div role="tabpanel" id="tabpanel-keywords" aria-labelledby="tab-keywords" className="space-y-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Add keywords that are associated with this project for better categorization and search.
                </div>

                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      placeholder="Enter keyword"
                      className="flex-1"
                      disabled={isFormDisabled}
                    />
                    <Button
                      type="button"
                      onClick={addKeyword}
                      size="sm"
                      disabled={!keywordInput.trim() || formData.keywords.includes(keywordInput.trim()) || isFormDisabled}
                    >
                      Add Keyword
                    </Button>
                  </div>

                  {formData.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                      {formData.keywords.map((keyword, index) => (
                        <span
                          key={`keyword-${index}-${keyword}`}
                          className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                        >
                          <span className="max-w-32 truncate" title={keyword}>{keyword}</span>
                          <button
                            type="button"
                            onClick={() => removeKeyword(index)}
                            className="ml-1 hover:opacity-70 focus:outline-none"
                            title="Remove keyword"
                            disabled={isFormDisabled}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.keywords.length}/100 keywords
                  </p>
                  {errors.keywords && (
                    <p className="text-sm text-red-600 dark:text-red-400">{errors.keywords}</p>
                  )}
                </div>
              </div>
            )}

            {/* Visibility Tab */}
            {activeTab === 'visibility' && (
              <div role="tabpanel" id="tabpanel-visibility" aria-labelledby="tab-visibility" className="space-y-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {groupsApiAvailable
                    ? "Select groups that should have visibility to this project. Leave empty for organization-wide visibility."
                    : "Groups API is not available. Project will have organization-wide visibility."
                  }
                </div>

                {groupsApiAvailable && groups.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-4 dark:border-gray-600">
                    {groups.filter(g => g.type === 'visibility').map(group => (
                      <div key={group._id || group.id} className="flex items-center py-2">
                        <input
                          type="checkbox"
                          id={`group-${group._id || group.id}`}
                          checked={formData.visibilityGroups.includes(group._id || group.id || '')}
                          onChange={() => handleGroupToggle(group._id || group.id || '')}
                          className="mr-3"
                          disabled={isFormDisabled}
                        />
                        <label
                          htmlFor={`group-${group._id || group.id}`}
                          className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                        >
                          {group.name}
                        </label>
                      </div>
                    ))}
                    {groups.filter(g => g.type === 'visibility').length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No visibility groups available.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {!groupsApiAvailable
                      ? "Groups functionality is not available."
                      : "No groups available for selection."
                    }
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'basic' && 'Fill in the basic project information'}
            {activeTab === 'conditions' && `${formData.conditions.sender.length + formData.conditions.subject.length + formData.conditions.body.length + formData.conditions.attachmentName.length + formData.conditions.attachmentContent.length} conditions added`}
            {activeTab === 'exclusions' && `${formData.exclusionConditions.sender.length + formData.exclusionConditions.senderDomain.length + formData.exclusionConditions.subject.length + formData.exclusionConditions.body.length + formData.exclusionConditions.attachmentName.length + formData.exclusionConditions.attachmentContent.length} exclusions added`}
            {activeTab === 'keywords' && `${formData.keywords.length} keywords added`}
            {activeTab === 'visibility' && `${formData.visibilityGroups.length} groups selected`}
          </div>
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isFormDisabled}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isFormDisabled}
              className="relative"
            >
              {isSubmitting && (
                <LoadingSpinner size="sm" className="mr-2" />
              )}
              {project ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};